import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Droplets, ShieldCheck, AlertTriangle, FileDown, Lock, Eye, Calendar, MapPin, Sparkles, Crown } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PropertyTimeline } from "@/components/PropertyTimeline";
import { formatDateAU } from "@/lib/formatDateAU";

const BAND = (score) => {
  if (score == null) return { label: "Pending", bg: "bg-slate-200", text: "text-slate-700", ring: "ring-slate-300" };
  if (score >= 90) return { label: "Excellent", bg: "bg-emerald-500", text: "text-white", ring: "ring-emerald-300" };
  if (score >= 75) return { label: "Good", bg: "bg-cyan-500", text: "text-white", ring: "ring-cyan-300" };
  if (score >= 60) return { label: "Fair", bg: "bg-amber-500", text: "text-white", ring: "ring-amber-300" };
  if (score >= 40) return { label: "Attention Required", bg: "bg-orange-500", text: "text-white", ring: "ring-orange-300" };
  return { label: "Action Required", bg: "bg-rose-500", text: "text-white", ring: "ring-rose-300" };
};

const ROLE_LABEL = {
  buyer: "Buyer", conveyancer: "Conveyancer", property_manager: "Property Manager",
  family: "Family Member", other: "Recipient",
};

export default function SharedPassport() {
  const { token } = useParams();
  const [preflight, setPreflight] = useState(null);  // {valid, password_required}
  const [password, setPassword] = useState("");
  const [passwordSubmitted, setPasswordSubmitted] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  // Preflight to know if we need a password
  useEffect(() => {
    setLoading(true);
    fetch(`${apiBase}/api/shared/passport/${token}/preflight`)
      .then(r => r.json())
      .then(p => { setPreflight(p); if (!p.valid || !p.password_required) loadData(p, null); else setLoading(false); })
      .catch(() => { setError("not_found"); setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async (pf, pw) => {
    setLoading(true); setError(null);
    try {
      let r;
      if (pw) {
        // Phase 2L-security-M4 — password is submitted in POST body,
        // never in a query string (keeps it out of browser history,
        // server access logs, and Referer headers).
        r = await fetch(`${apiBase}/api/shared/passport/${token}/unlock`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: pw }),
        });
      } else {
        r = await fetch(`${apiBase}/api/shared/passport/${token}`);
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setError(body.detail || `http_${r.status}`);
        setData(null);
      } else {
        setData(await r.json());
      }
    } catch (e) {
      setError("network");
    } finally {
      setLoading(false);
      setPasswordSubmitted(true);
    }
  };

  const submitPassword = (e) => {
    e?.preventDefault();
    loadData(preflight, password);
  };

  const downloadCertificate = async () => {
    // Phase 2L-security-M4 — password (when present) is sent in POST
    // body. For non-password shares we keep the GET so a static link
    // still works.
    let r;
    if (password) {
      r = await fetch(`${apiBase}/api/shared/passport/${token}/certificate.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
    } else {
      r = await fetch(`${apiBase}/api/shared/passport/${token}/certificate.pdf`);
    }
    if (!r.ok) return;
    const blob = await r.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `water-history-${(data?.property?.id || "property").slice(0,8)}.pdf`; a.click();
  };

  // === RENDER STATES ===
  if (loading) return <PublicShell><Loading /></PublicShell>;

  if (!preflight?.valid) {
    return <PublicShell><Unavailable reason={preflight?.reason || "not_found"} /></PublicShell>;
  }

  if (preflight.password_required && !data) {
    return (
      <PublicShell>
        <div data-testid="shared-password-prompt" className="max-w-md mx-auto bg-white rounded-3xl border border-slate-200 p-8">
          <Lock className="w-9 h-9 text-cyan-700 mx-auto" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4 text-center">Password required</h1>
          <p className="text-sm text-slate-500 mt-1 text-center">The owner has password-protected this AWARE Water Health Record share link.</p>
          <form onSubmit={submitPassword} className="mt-6 space-y-3">
            <Input data-testid="shared-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" autoFocus />
            {passwordSubmitted && error === "wrong_password" && <p className="text-sm text-rose-600">Wrong password — please try again.</p>}
            <Button data-testid="shared-password-submit" type="submit" className="btn-aqua rounded-full font-semibold w-full">Unlock</Button>
          </form>
        </div>
      </PublicShell>
    );
  }

  if (!data) return <PublicShell><Unavailable reason={error || "not_found"} /></PublicShell>;

  const { property: p, tests, health_score, ai_summary, timeline, share, membership_state, certificate_available } = data;
  const band = BAND(health_score);

  return (
    <PublicShell>
      <div data-testid="shared-passport" className="max-w-5xl mx-auto">
        {/* Banner */}
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-cyan-900 text-white p-6 lg:p-8 relative overflow-hidden">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-300">
            <ShieldCheck className="w-3.5 h-3.5" /> Shared AWARE Water Health Record
          </div>
          <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 data-testid="shared-property-address" className="font-display text-2xl lg:text-3xl font-bold leading-tight">{p.address}</h1>
              <p className="text-slate-300 text-sm mt-1 flex items-center gap-1.5 flex-wrap">
                <MapPin className="w-3.5 h-3.5" /> {p.property_type}
                <span className="opacity-50">·</span>
                <span>Passport ID <span className="font-mono">{p.id.slice(0, 8)}</span></span>
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs bg-white/10 border border-white/15 rounded-full px-3 py-1.5">
              <Eye className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-cyan-100">Read-only view for {ROLE_LABEL[share?.recipient_role] || "you"}</span>
            </div>
          </div>
        </div>

        {/* Score banner */}
        <div className={`mt-5 rounded-3xl ${band.bg} ${band.text} p-7 flex items-center justify-between gap-5 flex-wrap shadow-lg`}>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-80">Water Health Score™</div>
            <div className="font-display text-5xl font-bold mt-2">{health_score != null ? health_score : "—"}<span className="text-2xl font-semibold opacity-80">/100</span></div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold opacity-80">Rating</div>
            <div className="font-display text-2xl font-bold mt-2">{band.label}</div>
            {tests?.[0]?.published_at && <div className="text-xs opacity-80 mt-0.5">Latest test {formatDateAU(tests[0].published_at)}</div>}
          </div>
        </div>

        {/* AI summary */}
        {ai_summary?.narrative && (
          <div data-testid="shared-ai-summary" className="mt-5 rounded-3xl bg-white border border-cyan-200 p-6 lg:p-7">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
              <Sparkles className="w-3.5 h-3.5" /> What these results mean
            </div>
            <p className="mt-3 text-slate-700 leading-relaxed">{ai_summary.narrative}</p>
            {ai_summary.key_findings?.length > 0 && (
              <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                {ai_summary.key_findings.map((f, i) => <li key={i} className="flex items-start gap-2"><span className="text-cyan-600 mt-1">•</span><span>{f}</span></li>)}
              </ul>
            )}
          </div>
        )}

        {/* Action: download certificate */}
        {certificate_available && (
          <Button data-testid="shared-download-cert" onClick={downloadCertificate} className="mt-5 btn-aqua rounded-full font-semibold">
            <FileDown className="w-4 h-4 mr-1.5" /> Download Property Water History Certificate
          </Button>
        )}

        {/* Membership state */}
        {membership_state === "active" && (
          <div className="mt-5 rounded-2xl bg-cyan-50 border border-cyan-200 p-3 flex items-center gap-2 text-sm">
            <Crown className="w-4 h-4 text-cyan-700" />
            <span className="text-cyan-900">This property is covered by an active AWARE Water Health Record membership.</span>
          </div>
        )}

        {/* Tests */}
        <div className="mt-7">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-3">Historical testing</h2>
          {tests.length === 0 ? (
            <p className="text-sm text-slate-500">No completed tests yet.</p>
          ) : (
            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Test</th><th className="text-left px-4 py-3">Sample</th><th className="text-right px-4 py-3">Score</th></tr>
                </thead>
                <tbody>
                  {tests.map(t => (
                    <tr key={t.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-700">{formatDateAU(t.published_at || t.collected_at)}</td>
                      <td className="px-4 py-3 text-slate-700">{t.test_name || t.test_type}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{t.sample_number || "—"}</td>
                      <td className="px-4 py-3 text-right font-bold text-slate-900">{t.health_score ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="mt-7">
          <PropertyTimeline events={timeline} />
        </div>

        <p className="mt-10 text-center text-xs text-slate-400">Aware Water Testing · AWARE Water Health Record shared view</p>
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <span className="text-xs text-slate-500 hidden sm:block">awarewatertesting.com.au</span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">{children}</main>
    </div>
  );
}

function Loading() {
  return (
    <div data-testid="shared-loading" className="min-h-[40vh] flex items-center justify-center text-slate-500"><Droplets className="w-5 h-5 mr-2 animate-pulse text-cyan-500" /> Loading Passport…</div>
  );
}

function Unavailable({ reason }) {
  const map = {
    not_found: { title: "Link not found", body: "This share link doesn't exist. Please ask the property owner for a new link." },
    expired: { title: "Link expired", body: "This share link has expired. Please ask the property owner for a new one." },
    revoked: { title: "Link revoked", body: "The property owner has revoked this share link." },
    network: { title: "Network error", body: "Couldn't reach the server. Please retry." },
  };
  const cfg = map[reason] || map.not_found;
  return (
    <div data-testid="shared-unavailable" className="max-w-md mx-auto bg-white rounded-3xl border border-slate-200 p-8 text-center">
      <AlertTriangle className="w-9 h-9 text-amber-500 mx-auto" />
      <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">{cfg.title}</h1>
      <p className="text-sm text-slate-500 mt-2">{cfg.body}</p>
    </div>
  );
}
